import React from 'react'
import PropTypes from 'prop-types'

import { COLORS } from '_constants/styles'

import Text from '../../template/components/Text.jsx'
import usePosition from '../../template/hooks/usePosition'
import { useLazyLoad } from '_hooks'

import './styles.scss'

const CustomizedAxisLabel = (props) => {
  const { y, payload, width } = props

  const [titleRef, titleRect] = usePosition()
  return (
    <svg x={0} y={y - titleRect.height / 2} width={width}>
      <Text
        gRef={titleRef}
        value={payload?.value?.toString?.()}
        fontSize={24}
        textAnchor={'end'}
        fill={'#3D3D3D'}
        width={width}
        x={0}
        y={0}
      />
    </svg>
  )
}

const labelFormatter = {
  percent: (value) => `${(value * 100).toFixed(0)}%`,
  overIndex: (value) => `${value.toFixed(2)}x`,
}

const BarChart = ({ barSize, data, height, margin, padding, width, valueType }) => {
  const dataFormatted = data.map((d, index) => {
    const previous = data.slice(0, index)
    const count = previous.filter((p) => p.key === d.key).length
    return { ...d, label: count ? `${d.key} (${count + 1})` : d.key }
  })

  const [
    { BarChart: BarChartRecharts, Bar, XAxis, YAxis, LabelList },
    loadingRecharts,
  ] = useLazyLoad(import('recharts'))

  return (
    <If condition={!loadingRecharts}>
      <BarChartRecharts
        width={width}
        height={height}
        data={dataFormatted}
        margin={margin}
        barSize={barSize}
        layout="vertical"
        className="bar-chart-recharts"
      >
        <defs>
          <linearGradient id="barChartGradient" x1="0" y1="0" x2="1" y2="0">
            <stop offset="5%" stopColor={COLORS.DS_DARK_GREEN_BASE} />
            <stop offset="95%" stopColor={COLORS.DS_DARK_GREEN_LIGHT} />
          </linearGradient>
        </defs>
        <YAxis
          dataKey="label"
          scale="point"
          padding={padding}
          width={250}
          interval={0}
          tick={<CustomizedAxisLabel />}
          type="category"
        />
        <XAxis dataKey="value" type="number" hide={true} />
        <Bar
          dataKey="value"
          fill="url(#barChartGradient)"
          background={{ fill: 'white' }}
          isAnimationActive={false}
        >
          <LabelList
            dataKey="value"
            formatter={labelFormatter[valueType]}
            position="insideMiddle"
            fill="white"
          />
        </Bar>
      </BarChartRecharts>
    </If>
  )
}

BarChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    })
  ).isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  barSize: PropTypes.number,
  margin: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    left: PropTypes.number,
    bottom: PropTypes.number,
  }),
  padding: PropTypes.shape({
    left: PropTypes.number,
    right: PropTypes.number,
  }),
  valueType: PropTypes.oneOf(['percent', 'overIndex']),
}

BarChart.defaultProps = {
  width: 640,
  height: 230,
  barSize: 60,
  margin: {
    top: 0,
    right: 0,
    left: 0,
    bottom: 0,
  },
  padding: {
    left: 50,
    right: 50,
  },
  valueType: 'percent',
}

export default BarChart
